import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { V3GameState, V3Player, CellState } from '../types/V3_Core';
import GameArenaUI from '../components/game/GameArenaUI';
import { Play, Pause, FastForward, Rewind, SkipBack, SkipForward, ArrowLeft } from 'lucide-react';

// Types for Replay Data (matching Backend)
interface ReplayHistoryEntry {
    turn: number;
    playerId: string;
    actionType: string;
    targetId?: string;
    cellId?: number;
    result?: string;
    payload?: any;
    timestamp: number;
}

interface ReplayGame {
    roomId: string;
    mode: string;
    players: {
        userId: string;
        pseudo: string;
        avatarId: string;
        isBot: boolean;
        score: number;
        rank: number;
    }[];
    initialState: Record<string, any>; // SetupData
    history: ReplayHistoryEntry[];
    playedAt: string;
}

const ReplayPage: React.FC = () => {
    const { gameId } = useParams<{ gameId: string }>();
    const navigate = useNavigate();

    const [game, setGame] = useState<ReplayGame | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [turnIndex, setTurnIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1000); // ms per turn

    // Fetch Replay Data
    useEffect(() => {
        const fetchReplay = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`http://localhost:3000/api/games/${gameId}/replay`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) throw new Error('Impossible de charger le replay');
                const data = await response.json();
                setGame(data.replay);
                setTurnIndex(0);
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (gameId) fetchReplay();
    }, [gameId]);

    // Playback Logic
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isPlaying && game) {
            interval = setInterval(() => {
                setTurnIndex(prev => {
                    if (prev >= game.history.length - 1) {
                        setIsPlaying(false);
                        return prev;
                    }
                    return prev + 1;
                });
            }, playbackSpeed);
        }
        return () => clearInterval(interval);
    }, [isPlaying, game, playbackSpeed]);

    // STATE RECONSTRUCTION ENGINE
    const reconstructedState = useMemo((): V3GameState | null => {
        if (!game) return null;

        // 1. Initialize Base State (Players, Empty Grids)
        const players: V3Player[] = game.players.map(p => ({
            id: p.userId,
            pseudo: p.pseudo,
            avatarId: p.avatarId || 'default',
            lucidity: 0, // Starts at 0
            cortex: { cells: Array(10).fill(null).map(() => Array(10).fill('EMPTY')) },
            souvenirs: [],
            memoryMap: {},
            isTargetable: true,
            isReady: true
        }));

        // 2. Apply Setup (Initial State)
        players.forEach(p => {
            const setup = game.initialState?.[p.id];
            if (setup) {
                // Apply Shadows
                (setup.shadowZones || []).forEach((idx: number) => {
                    const r = Math.floor(idx / 10);
                    const c = idx % 10;
                    p.cortex.cells[r][c] = 'SHADOW_ZONE';
                });
                // Apply Souvenirs
                (setup.souvenirs || []).forEach((s: any, i: number) => {
                    // Reconstruct Souvenir Object
                    // Note: We don't have the full object but we know positions
                    (s.cells || []).forEach((idx: number) => {
                        const r = Math.floor(idx / 10);
                        const c = idx % 10;
                        p.cortex.cells[r][c] = 'SOUVENIR';
                    });
                });
            }
        });

        // 3. Apply History Steps up to TurnIndex
        const currentLog: string[] = [];
        let activePlayerId = players[0]?.id;
        let lastTargetId: string | undefined = undefined;
        let currentVerdictQuestion: string | undefined = undefined;
        let currentSouvenirRevealed: string | undefined = undefined;
        let phase: 'SETUP' | 'PLAYING' | 'VERDICT' | 'FINISHED' = 'PLAYING';

        const historySlice = game.history.slice(0, turnIndex + 1);

        historySlice.forEach(entry => {
            const actor = players.find(p => p.id === entry.playerId);
            const target = players.find(p => p.id === entry.targetId);

            if (entry.actionType === 'PROBE' && actor && target && entry.cellId !== undefined) {
                activePlayerId = entry.playerId;
                lastTargetId = entry.targetId;

                const r = Math.floor(entry.cellId / 10);
                const c = entry.cellId % 10;

                // Visual Update Logic
                if (entry.result === 'HIT' || entry.result === 'SUNK') {
                    target.cortex.cells[r][c] = 'REVEALED_HIT';
                    // Score Update (Simple approx)
                    actor.lucidity += (entry.result === 'SUNK' ? 25 : 5);
                } else {
                    target.cortex.cells[r][c] = 'REVEALED_EMPTY';
                    if (entry.result === 'SHADOW') {
                        actor.lucidity = Math.max(0, actor.lucidity - 10);
                    }
                }

                if (entry.result === 'SUNK') {
                    phase = 'VERDICT';
                    currentVerdictQuestion = "Question de Verdict..."; // We don't have payload in setup data easily, ideally payload has it
                    currentVerdictQuestion = "Question de Verdict...";
                    if (entry.payload?.question) currentVerdictQuestion = entry.payload.question;
                }

                if (entry.payload?.message) currentLog.push(entry.payload.message);
            }

            if (entry.actionType === 'VERDICT' && actor) {
                phase = 'PLAYING';
                if (entry.result === 'CONFIRMED') {
                    actor.lucidity += 20;
                    currentLog.push(`Verdict Confirmé pour ${actor.pseudo}`);
                } else {
                    actor.lucidity = Math.max(0, actor.lucidity - 5);
                    currentLog.push(`Verdict Rejeté pour ${actor.pseudo}`);
                }
            }
            // Winner handling? 
            if (actor && actor.lucidity >= 100) phase = 'FINISHED';
        });

        return {
            roomId: game.roomId,
            phase,
            activePlayerId,
            players,
            log: currentLog,
            currentVerdictQuestion,
            currentSouvenirRevealed,
            lastTargetId,
            winnerId: phase === 'FINISHED' ? activePlayerId : undefined
        };
    }, [game, turnIndex]);


    if (loading) return <div className="h-screen flex items-center justify-center bg-slate-950 text-cyan-500 font-mono animate-pulse">CHARGEMENT DU REPLAY...</div>;
    if (error || !game) return <div className="h-screen flex items-center justify-center bg-slate-950 text-red-500 font-mono">CODE ERREUR: {error || 'NOT_FOUND'}</div>;

    return (
        <div className="h-screen w-full flex flex-col bg-slate-950 relative">
            {/* Header */}
            <div className="absolute top-4 left-4 z-50">
                <button onClick={() => navigate('/dashboard')} className="flex items-center text-slate-400 hover:text-white transition-colors">
                    <ArrowLeft size={20} className="mr-2" /> Retour
                </button>
            </div>

            {/* Main Game Area (Reused) */}
            <div className="flex-1 relative">
                {reconstructedState && (
                    <GameArenaUI
                        v3State={reconstructedState}
                        localPlayerId={reconstructedState.players[0].id} // Default to first player (or allow cycling?)
                        onProbe={() => { }} // Read-only
                        onVerdict={() => { }} // Read-only
                        revealAll={true} // SPECTATOR MODE
                    />
                )}

                {/* Overlay for "REPLAY MODE" */}
                <div className="absolute top-4 right-4 z-50 pointer-events-none">
                    <div className="px-3 py-1 bg-red-900/80 border border-red-500 rounded text-red-100 text-xs font-mono font-bold animate-pulse">
                        ● REPLAY MODE
                    </div>
                </div>
            </div>

            {/* Controls Bar */}
            <div className="h-24 bg-slate-900 border-t border-slate-700 p-4 flex flex-col gap-2 z-50">
                {/* Timeline */}
                <div className="w-full flex items-center gap-4">
                    <span className="text-xs font-mono text-slate-500 w-12 text-right">TURN {turnIndex}</span>
                    <input
                        type="range"
                        min={0}
                        max={game.history.length > 0 ? game.history.length - 1 : 0}
                        value={turnIndex}
                        onChange={(e) => {
                            setTurnIndex(Number(e.target.value));
                            setIsPlaying(false);
                        }}
                        className="flex-1 accent-amber-500 h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-xs font-mono text-slate-500 w-12">{game.history.length}</span>
                </div>

                {/* Buttons */}
                <div className="flex justify-center items-center gap-6">
                    <button onClick={() => setTurnIndex(0)} className="text-slate-400 hover:text-white"><SkipBack size={20} /></button>
                    <button onClick={() => setTurnIndex(t => Math.max(0, t - 1))} className="text-slate-400 hover:text-white"><Rewind size={20} /></button>

                    <button
                        onClick={() => setIsPlaying(!isPlaying)}
                        className={`
                            w-12 h-12 rounded-full flex items-center justify-center transition-all
                            ${isPlaying ? 'bg-amber-500 text-black shadow-[0_0_15px_#fbbf24]' : 'bg-slate-700 text-white hover:bg-slate-600'}
                        `}
                    >
                        {isPlaying ? <Pause size={24} fill="currentColor" /> : <Play size={24} fill="currentColor" className="ml-1" />}
                    </button>

                    <button onClick={() => setTurnIndex(t => Math.min(game.history.length - 1, t + 1))} className="text-slate-400 hover:text-white"><FastForward size={20} /></button>
                    <button onClick={() => setTurnIndex(game.history.length - 1)} className="text-slate-400 hover:text-white"><SkipForward size={20} /></button>

                    {/* Speed Toggle */}
                    <div className="w-px h-8 bg-slate-700 mx-4"></div>
                    <button
                        onClick={() => setPlaybackSpeed(s => s === 1000 ? 500 : (s === 500 ? 200 : 1000))}
                        className="text-xs font-mono font-bold text-amber-500 w-12"
                    >
                        {playbackSpeed === 1000 ? '1x' : (playbackSpeed === 500 ? '2x' : '5x')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReplayPage;
